import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import type { Clip, Composition, MediaAsset } from '@cinegrid/core';
import { importLocalFile } from '../../codec/importMedia';
import { resolveAssetUrl } from '../../media/mediaStore';
import { getPosterThumb } from '../../media/thumbnailCache';
import { useEditorStore } from '../../state/editorStore';
import { RenameDialog, InterpretFrameRateDialog, MediaPropertiesDialog } from '../controls/AppDialog';
import { PanelShell } from '../controls/PanelShell';

type MenuState =
  | { kind: 'new'; x: number; y: number }
  | { kind: 'ctx'; x: number; y: number; submenu?: 'interpret' }
  | { kind: 'comp'; x: number; y: number; compositionId: string }
  | null;

/** Prefer video/image under the playhead-ish start, else first visual clip. */
function pickCompThumbAsset(
  clips: Clip[],
  assetsById: Map<string, MediaAsset>
): MediaAsset | null {
  const sorted = [...clips].sort((a, b) => a.start - b.start);
  for (const clip of sorted) {
    if (!clip.assetId) continue;
    const asset = assetsById.get(clip.assetId);
    if (!asset) continue;
    if (asset.kind === 'video' || asset.kind === 'image') return asset;
  }
  for (const clip of sorted) {
    if (!clip.assetId) continue;
    const asset = assetsById.get(clip.assetId);
    if (asset?.kind === 'solid') return asset;
  }
  return null;
}

function CompositionThumb({
  composition,
  assets,
}: {
  composition: Composition;
  assets: MediaAsset[];
}) {
  const assetsById = useMemo(
    () => new Map(assets.map((a) => [a.id, a])),
    [assets]
  );
  const thumbAsset = useMemo(
    () => pickCompThumbAsset(composition.clips, assetsById),
    [composition.clips, assetsById]
  );

  if (!thumbAsset) {
    return (
      <div className="flex h-9 w-12 shrink-0 items-center justify-center border border-dashed border-hairline text-2xs uppercase text-text-low">
        CMP
      </div>
    );
  }

  return (
    <div className="relative h-9 w-12 shrink-0 overflow-hidden">
      <MediaAssetThumb asset={thumbAsset} />
      <div className="pointer-events-none absolute bottom-0 right-0 bg-black/65 px-0.5 text-[8px] font-semibold uppercase leading-none text-white">
        Comp
      </div>
    </div>
  );
}

function MediaAssetThumb({ asset }: { asset: MediaAsset }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setSrc(null);

    if (asset.kind === 'adjustment' || asset.kind === 'solid' || asset.kind === 'audio') {
      return () => {
        alive = false;
      };
    }

    if (asset.kind === 'image') {
      void resolveAssetUrl(asset)
        .then((url) => {
          if (alive) setSrc(url);
        })
        .catch(() => undefined);
      return () => {
        alive = false;
      };
    }

    // video
    void getPosterThumb(asset)
      .then((url) => {
        if (alive && url) setSrc(url);
      })
      .catch(() => undefined);

    return () => {
      alive = false;
    };
  }, [asset.id, asset.kind, asset.url, asset.duration, asset.solidColor]);

  if (asset.kind === 'adjustment') {
    return (
      <div
        className="h-9 w-12 shrink-0 bg-[var(--cg-bg-hover)]"
        aria-hidden
      />
    );
  }

  if (asset.kind === 'solid') {
    return (
      <div
        className="h-9 w-12 shrink-0"
        style={{ background: asset.solidColor || '#808080' }}
        aria-hidden
      />
    );
  }

  if (asset.kind === 'audio') {
    return (
      <div className="flex h-9 w-12 shrink-0 items-end justify-center gap-px bg-[var(--cg-bg-base)] px-1.5 pb-1.5">
        {[40, 70, 45, 85, 55, 65].map((h, i) => (
          <span
            key={i}
            className="w-[2px] bg-[var(--cg-text-low)] opacity-70"
            style={{ height: `${h}%` }}
            aria-hidden
          />
        ))}
      </div>
    );
  }

  return (
    <div className="relative h-9 w-12 shrink-0 overflow-hidden bg-[var(--cg-bg-base)]">
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" draggable={false} />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-2xs uppercase text-text-low">
          {asset.kind.slice(0, 3)}
        </div>
      )}
    </div>
  );
}

function MediaBinAssetRow({
  asset,
  selected,
  onSelect,
  onToggle,
  onOpen,
  onContextMenu,
}: {
  asset: MediaAsset;
  selected: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onOpen: () => void;
  onContextMenu: (e: ReactMouseEvent) => void;
}) {
  const rowRef = useRef<HTMLLIElement>(null);

  const disarmDrag = () => {
    const el = rowRef.current;
    if (el) el.draggable = false;
  };

  return (
    <li
      ref={rowRef}
      data-media-asset
      role="option"
      aria-selected={selected}
      tabIndex={0}
      className={`flex cursor-default select-none items-center gap-2 border-l-2 px-2 py-2 outline-none ${
        selected
          ? 'border-accent bg-raised text-text-high'
          : 'border-transparent hover:bg-raised'
      }`}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        // Stop parent handlers from eating the gesture
        e.stopPropagation();
        if (e.shiftKey || e.metaKey || e.ctrlKey) onToggle();
        else onSelect();
        // Arm HTML5 drag only after selection (imperative — React remounts won't stick mid-gesture)
        const el = rowRef.current;
        if (el) el.draggable = true;
      }}
      onPointerUp={disarmDrag}
      onPointerCancel={disarmDrag}
      onDragStart={(e) => {
        e.dataTransfer.setData('application/cinegrid-asset', asset.id);
        e.dataTransfer.effectAllowed = 'copy';
        // Ensure this asset is selected when a drag begins
        if (!selected) onSelect();
      }}
      onDragEnd={disarmDrag}
      onClick={(e) => {
        // Fallback when browsers skip selection due to drag heuristics
        e.stopPropagation();
        if (e.shiftKey || e.metaKey || e.ctrlKey) onToggle();
        else onSelect();
      }}
      onDoubleClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen();
      }}
      onContextMenu={onContextMenu}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <MediaAssetThumb asset={asset} />
      <div className="min-w-0 flex-1 pointer-events-none">
        <div className="truncate text-xs text-text-high">{asset.name}</div>
        <div className="timecode text-2xs text-text-low">
          {asset.kind === 'adjustment' || asset.kind === 'solid'
            ? `${asset.duration.toFixed(1)}s · ${asset.kind}`
            : asset.kind === 'audio'
              ? `${asset.duration.toFixed(1)}s · audio`
              : `${asset.duration.toFixed(1)}s${
                  asset.width ? ` · ${asset.width}×${asset.height}` : ''
                }${asset.fps > 0 ? ` · ${asset.fps % 1 ? asset.fps.toFixed(2) : asset.fps}fps` : ''}`}
        </div>
      </div>
    </li>
  );
}

export function MediaBinPanel() {
  const assets = useEditorStore((s) => s.project.assets);
  const selected = useEditorStore((s) => s.mediaSelection);
  const setMediaSelection = useEditorStore((s) => s.setMediaSelection);
  const addAsset = useEditorStore((s) => s.addAsset);
  const removeAssets = useEditorStore((s) => s.removeAssets);
  const interpretAssetFrameRate = useEditorStore((s) => s.interpretAssetFrameRate);
  const createAdjustmentAsset = useEditorStore((s) => s.createAdjustmentAsset);
  const createSolidAsset = useEditorStore((s) => s.createSolidAsset);
  const createComposition = useEditorStore((s) => s.createComposition);
  const setActiveComposition = useEditorStore((s) => s.setActiveComposition);
  const renameComposition = useEditorStore((s) => s.renameComposition);
  const duplicateComposition = useEditorStore((s) => s.duplicateComposition);
  const removeComposition = useEditorStore((s) => s.removeComposition);
  const setInspectorTab = useEditorStore((s) => s.setInspectorTab);
  const compositions = useEditorStore((s) => s.project.compositions ?? []);
  const activeCompositionId = useEditorStore((s) => s.project.activeCompositionId);
  const addClipFromAsset = useEditorStore((s) => s.addClipFromAsset);
  const tracks = useEditorStore((s) => s.project.tracks);
  const playhead = useEditorStore((s) => s.playhead);
  const inputRef = useRef<HTMLInputElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const [menu, setMenu] = useState<MenuState>(null);
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);
  const [interpretOpen, setInterpretOpen] = useState(false);
  const [propertiesAssetId, setPropertiesAssetId] = useState<string | null>(null);

  // Drop stale ids when assets are deleted
  useEffect(() => {
    const ids = new Set(assets.map((a) => a.id));
    const next = selected.filter((id) => ids.has(id));
    if (next.length !== selected.length) setMediaSelection(next);
  }, [assets, selected, setMediaSelection]);

  const selectOne = (id: string) => setMediaSelection([id]);
  const toggleOne = (id: string) =>
    setMediaSelection(
      selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]
    );

  const ingest = async (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      if (
        !file.type.startsWith('video/') &&
        !file.type.startsWith('audio/') &&
        !file.type.startsWith('image/')
      ) {
        continue;
      }
      try {
        const asset = await importLocalFile(file);
        addAsset(asset);
      } catch (err) {
        console.error('Import failed', err);
      }
    }
  };

  const openImport = () => inputRef.current?.click();

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) void ingest(e.dataTransfer.files);
  };

  const pickTrackForAsset = (kind: string) => {
    const wantAudio = kind === 'audio';
    const candidates = tracks.filter((t) =>
      wantAudio ? t.kind === 'audio' : t.kind === 'video'
    );
    if (kind === 'adjustment' || kind === 'solid') {
      return (
        candidates.find((t) => /^v2$/i.test(t.name.trim())) ||
        candidates[0]
      );
    }
    return (
      candidates.find((t) => /^v1$/i.test(t.name.trim())) ||
      candidates.find((t) => /^a1$/i.test(t.name.trim())) ||
      [...candidates].reverse().find((t) => t.kind === 'video') ||
      candidates[0]
    );
  };

  const addToTimeline = (assetId: string) => {
    const a = assets.find((x) => x.id === assetId);
    if (!a) return;
    const track = pickTrackForAsset(a.kind);
    if (track) addClipFromAsset(a.id, track.id, playhead);
  };

  useEffect(() => {
    const el = colorInputRef.current;
    if (!el) return;
    const onChange = () => createSolidAsset(el.value);
    el.addEventListener('change', onChange);
    return () => el.removeEventListener('change', onChange);
  }, [createSolidAsset]);

  useEffect(() => {
    if (!menu) return;
    const close = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('[data-media-menu]')) return;
      setMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null);
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const newMenuItems = (
    <>
      <button
        type="button"
        className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
        onClick={() => {
          createComposition();
          setMenu(null);
        }}
      >
        Composition / Timeline
      </button>
      <button
        type="button"
        className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
        onClick={() => {
          createAdjustmentAsset();
          setMenu(null);
        }}
      >
        Adjustment Layer
      </button>
      <button
        type="button"
        className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
        onClick={() => {
          setMenu(null);
          colorInputRef.current?.click();
        }}
      >
        Solid Layer…
      </button>
    </>
  );

  const primarySelected =
    assets.find((a) => a.id === selected[0]) ??
    (propertiesAssetId ? assets.find((a) => a.id === propertiesAssetId) : undefined);

  const interpretTargets = selected.length
    ? assets.filter(
        (a) => selected.includes(a.id) && (a.kind === 'video' || a.kind === 'image')
      )
    : [];

  const canInterpret = interpretTargets.length > 0;

  return (
    <PanelShell
      title="Media"
      actions={
        <button type="button" className="text-2xs text-accent" onClick={openImport}>
          Import
        </button>
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept="video/*,audio/*,image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) void ingest(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={colorInputRef}
        type="color"
        defaultValue="#808080"
        className="pointer-events-none fixed h-0 w-0 opacity-0"
      />

      <div
        className="flex h-full min-h-0 flex-col"
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDrop={onDrop}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if ((e.target as HTMLElement).closest('[data-media-asset]')) return;
          if ((e.target as HTMLElement).closest('[data-media-comp]')) return;
          setMenu({ kind: 'ctx', x: e.clientX, y: e.clientY });
        }}
        onDoubleClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest('[data-media-asset]')) return;
          if (target.closest('[data-media-comp]')) return;
          if (target.closest('[data-media-footer]')) return;
          openImport();
        }}
      >
        <ul
          className="min-h-0 flex-1 divide-y divide-hairline overflow-auto"
          role="listbox"
          aria-label="Media"
          aria-multiselectable
          onPointerDown={(e) => {
            const t = e.target as HTMLElement;
            if (t.closest('[data-media-asset]') || t.closest('[data-media-comp]')) return;
            setMediaSelection([]);
          }}
        >
          <li className="border-b border-hairline bg-raised/40 px-2 py-1 text-2xs font-semibold uppercase tracking-wider text-text-low">
            Compositions
          </li>
          {compositions.map((comp) => {
            const active = comp.id === activeCompositionId;
            return (
              <li
                key={comp.id}
                data-media-comp
                className={`flex cursor-pointer items-center gap-2 border-l-2 px-2 py-2 ${
                  active
                    ? 'border-accent bg-accent/20'
                    : 'border-transparent hover:bg-raised'
                }`}
                onClick={() => setActiveComposition(comp.id)}
                onDoubleClick={() => setActiveComposition(comp.id)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenu({
                    kind: 'comp',
                    x: e.clientX,
                    y: e.clientY,
                    compositionId: comp.id,
                  });
                }}
              >
                <CompositionThumb composition={comp} assets={assets} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs text-text-high">
                    {comp.name}
                    {active ? (
                      <span className="ml-1 text-2xs text-accent">· active</span>
                    ) : null}
                  </div>
                  <div className="timecode text-2xs text-text-low">
                    {comp.settings.width}×{comp.settings.height} · {comp.settings.frameRate}fps ·{' '}
                    {comp.clips.length} clips
                  </div>
                </div>
              </li>
            );
          })}

          <li className="border-b border-t border-hairline bg-raised/40 px-2 py-1 text-2xs font-semibold uppercase tracking-wider text-text-low">
            Media
          </li>
          {assets.length === 0 && (
            <li className="p-3 text-2xs text-text-low">
              Drop files here, double-click to import, or use + / right-click → New
            </li>
          )}
          {assets.map((a) => (
            <MediaBinAssetRow
              key={a.id}
              asset={a}
              selected={selected.includes(a.id)}
              onSelect={() => selectOne(a.id)}
              onToggle={() => toggleOne(a.id)}
              onOpen={() => addToTimeline(a.id)}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!selected.includes(a.id)) selectOne(a.id);
                setMenu({ kind: 'ctx', x: e.clientX, y: e.clientY });
              }}
            />
          ))}
        </ul>

        <div
          data-media-footer
          className="flex shrink-0 items-center gap-1 border-t border-hairline px-2 py-1.5"
        >
          <span className="min-w-0 flex-1 truncate text-2xs text-text-low">
            {selected.length > 0 ? `${selected.length} selected` : ''}
          </span>
          <button
            type="button"
            title="New"
            className="inline-flex h-7 w-7 items-center justify-center border border-hairline text-sm text-text-high hover:border-accent hover:text-accent"
            onClick={(e) => {
              e.stopPropagation();
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              setMenu({ kind: 'new', x: rect.right - 160, y: rect.top - 4 });
            }}
          >
            +
          </button>
          <button
            type="button"
            title="Delete selected"
            disabled={selected.length === 0}
            className="inline-flex h-7 w-7 items-center justify-center border border-hairline text-text-low hover:border-danger hover:text-danger disabled:opacity-30"
            onClick={() => {
              removeAssets(selected);
              setMediaSelection([]);
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <line x1="10" y1="11" x2="10" y2="17" />
              <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
          </button>
        </div>
      </div>

      {menu && (
        <div
          data-media-menu
          className="fixed z-[100] min-w-[180px] border border-hairline bg-panel py-1"
          style={{
            left: menu.x,
            top: menu.kind === 'new' ? undefined : menu.y,
            bottom: menu.kind === 'new' ? window.innerHeight - menu.y : undefined,
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {menu.kind === 'comp' ? (
            <>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
                onClick={() => {
                  setActiveComposition(menu.compositionId);
                  setMenu(null);
                }}
              >
                Open
              </button>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
                onClick={() => {
                  setActiveComposition(menu.compositionId);
                  setInspectorTab('comp');
                  setMenu(null);
                }}
              >
                Comp Settings
              </button>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
                onClick={() => {
                  const current = compositions.find((c) => c.id === menu.compositionId);
                  setRenameTarget({
                    id: menu.compositionId,
                    name: current?.name ?? 'Comp',
                  });
                  setMenu(null);
                }}
              >
                Rename…
              </button>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
                onClick={() => {
                  duplicateComposition(menu.compositionId);
                  setMenu(null);
                }}
              >
                Duplicate
              </button>
              <button
                type="button"
                disabled={compositions.length <= 1}
                className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-danger disabled:opacity-30"
                onClick={() => {
                  removeComposition(menu.compositionId);
                  setMenu(null);
                }}
              >
                Delete
              </button>
            </>
          ) : menu.kind === 'ctx' ? (
            <>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
                disabled={!primarySelected}
                onClick={() => {
                  if (primarySelected) addToTimeline(primarySelected.id);
                  setMenu(null);
                }}
              >
                Add to Timeline
              </button>
              <div className="relative">
                <button
                  type="button"
                  disabled={!canInterpret}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent disabled:opacity-30"
                  onMouseEnter={() => {
                    if (canInterpret) setMenu({ ...menu, submenu: 'interpret' });
                  }}
                  onClick={() => {
                    if (!canInterpret) return;
                    setMenu({
                      ...menu,
                      submenu: menu.submenu === 'interpret' ? undefined : 'interpret',
                    });
                  }}
                >
                  Interpret Footage
                  <span className="ml-3 text-text-low">▸</span>
                </button>
                {menu.submenu === 'interpret' && canInterpret ? (
                  <div
                    data-media-menu
                    className="absolute left-full top-0 z-[101] min-w-[160px] border border-hairline bg-panel py-1"
                    onMouseEnter={() => setMenu({ ...menu, submenu: 'interpret' })}
                  >
                    <button
                      type="button"
                      className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
                      onClick={() => {
                        setMenu(null);
                        setInterpretOpen(true);
                      }}
                    >
                      Change Frame Rate…
                    </button>
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
                disabled={!primarySelected}
                onClick={() => {
                  if (primarySelected) setPropertiesAssetId(primarySelected.id);
                  setMenu(null);
                }}
              >
                Properties…
              </button>
              <div className="my-1 border-t border-hairline" />
              <button
                type="button"
                disabled={selected.length === 0}
                className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-danger disabled:opacity-30"
                onClick={() => {
                  removeAssets(selected);
                  setMediaSelection([]);
                  setMenu(null);
                }}
              >
                Delete
              </button>
            </>
          ) : (
            <>
              <div className="px-3 py-1 text-2xs font-semibold uppercase tracking-wider text-text-low">
                New
              </div>
              {newMenuItems}
            </>
          )}
        </div>
      )}

      <RenameDialog
        open={!!renameTarget}
        title="Rename Composition"
        initialValue={renameTarget?.name ?? ''}
        onClose={() => setRenameTarget(null)}
        onSubmit={(name) => {
          if (renameTarget) renameComposition(renameTarget.id, name);
          setRenameTarget(null);
        }}
      />

      <InterpretFrameRateDialog
        open={interpretOpen}
        assetName={
          interpretTargets.length > 1
            ? `${interpretTargets.length} items`
            : interpretTargets[0]?.name ?? primarySelected?.name ?? 'Footage'
        }
        currentFps={interpretTargets[0]?.fps ?? primarySelected?.fps ?? 30}
        nativeFps={
          interpretTargets[0]?.nativeFps ??
          interpretTargets[0]?.fps ??
          primarySelected?.nativeFps ??
          primarySelected?.fps ??
          30
        }
        onClose={() => setInterpretOpen(false)}
        onSubmit={(fps) => {
          const ids = interpretTargets.map((a) => a.id);
          if (ids.length) interpretAssetFrameRate(ids, fps);
          setInterpretOpen(false);
        }}
      />

      <MediaPropertiesDialog
        open={!!propertiesAssetId}
        asset={assets.find((a) => a.id === propertiesAssetId) ?? null}
        onClose={() => setPropertiesAssetId(null)}
      />
    </PanelShell>
  );
}
